import { AppraisalRatingBand, AppraisalRatingScale } from '../models/staff-module';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Page } from '../models/common';
import {
  ApiPagination,
  ApiResponse,
  AppraisalCategory,
  AppraisalCategoryQuery,
  AppraisalCategoryRequest,
  ComplianceBulkReminderResult, ComplianceReminderResult,
  ComplianceRosterQuery,
  ComplianceRosterRow,
  ComplianceSummary,
  LeaveCalendarEntry,
  LeaveCalendarQuery,
  LeaveDecision,
  LeavePolicy,
  LeavePolicyRequest,
  LeaveRequest,
  LeaveRequestCreate,
  LeaveRequestQuery,
  RelievingOfficerOption,
  PagedQuery,
  PagedResult,
  PolicyAcknowledgement,
  PolicyCategory,
  StaffListItem,
  StaffListOption,
  StaffListRequest,
  StaffListSlug,
  StaffLeaveBalance,
  StaffPolicy,
  StaffPolicyQuery,
  StaffPolicyRequest,
  TeamBalances,
  TeamBalancesQuery,
} from '../models/staff-module';

/** Error surfaced to components: ready-to-display message plus per-field messages from a 400. */
export interface StaffModuleError {
  status: number;
  message: string;
  fieldErrors?: Record<string, string>;
}

/** Maps the API pagination block onto the `Page` shape the shared table/pagination UI expects. */
export function toPageProp(pagination: ApiPagination | undefined, pageRequest: { page: number; pageSize: number } & Record<string, any>): Page {
  return {
    ...(pagination as any),
    totalPages: pagination?.totalPages ?? 0,
    totalRecords: pagination?.totalElements ?? 0,
    pageNumber: (pagination?.page ?? 1),
    pageSize: pagination?.pageSize ?? pageRequest.pageSize,
    pageRequest,
  } as Page;
}

@Injectable({ providedIn: 'root' })
export class StaffModuleApiService {
  private readonly root = `${environment.baseUrl}${environment.coreUrl}staff-module`;
  private readonly staffs = `${environment.baseUrl}${environment.coreUrl}staffs`;

  constructor(private http: HttpClient) {}

  // ---- Lists ---------------------------------------------------------------
  listItems(slug: StaffListSlug, query: PagedQuery = {}): Observable<PagedResult<StaffListItem>> {
    return this.paged<StaffListItem>(`${this.root}/lists/${slug}`, query);
  }
  listOptions(slug: StaffListSlug): Observable<StaffListOption[]> {
    return this.http.get<ApiResponse<StaffListOption[]>>(`${this.root}/lists/${slug}/options`).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  getListItem(slug: StaffListSlug, id: number): Observable<StaffListItem> {
    return this.http.get<ApiResponse<StaffListItem>>(`${this.root}/lists/${slug}/${id}`).pipe(this.unwrap(), map(r => r.data as StaffListItem));
  }
  createListItem(slug: StaffListSlug, body: StaffListRequest): Observable<StaffListItem> {
    return this.http.post<ApiResponse<StaffListItem>>(`${this.root}/lists/${slug}`, body).pipe(this.unwrap(), map(r => r.data as StaffListItem));
  }
  updateListItem(slug: StaffListSlug, id: number, body: StaffListRequest): Observable<StaffListItem> {
    return this.http.put<ApiResponse<StaffListItem>>(`${this.root}/lists/${slug}/${id}`, body).pipe(this.unwrap(), map(r => r.data as StaffListItem));
  }
  // ---- Salary grades (ordered: level 1 is the highest; every call returns the whole scale) ----
  salaryGrades(): Observable<StaffListItem[]> {
    return this.http.get<ApiResponse<StaffListItem[]>>(`${this.root}/lists/salary-grades`).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  createSalaryGrade(body: StaffListRequest): Observable<StaffListItem[]> {
    return this.http.post<ApiResponse<StaffListItem[]>>(`${this.root}/lists/salary-grades`, body).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** Renames only: the grade keeps its level, and any `level` in the body is ignored. */
  updateSalaryGrade(id: number, body: StaffListRequest): Observable<StaffListItem[]> {
    return this.http.put<ApiResponse<StaffListItem[]>>(`${this.root}/lists/salary-grades/${id}`, body).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** The up/down arrows. */
  moveSalaryGrade(id: number, level: number): Observable<StaffListItem[]> {
    return this.http.put<ApiResponse<StaffListItem[]>>(`${this.root}/lists/salary-grades/${id}/level`, { level }).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** 409 while staff are still on the grade. Closes the gap and returns the remaining scale. */
  deleteSalaryGrade(id: number): Observable<StaffListItem[]> {
    return this.http.delete<ApiResponse<StaffListItem[]>>(`${this.root}/lists/salary-grades/${id}`).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  deleteListItem(slug: StaffListSlug, id: number): Observable<void> {
    return this.http.delete<ApiResponse>(`${this.root}/lists/${slug}/${id}`).pipe(this.unwrap(), map(() => undefined));
  }

  // ---- Performance appraisal ----------------------------------------------
  appraisalCategories(query: AppraisalCategoryQuery = {}): Observable<PagedResult<AppraisalCategory>> {
    return this.paged<AppraisalCategory>(`${this.root}/appraisal/categories`, query);
  }
  getAppraisalCategory(id: number): Observable<AppraisalCategory> {
    return this.http.get<ApiResponse<AppraisalCategory>>(`${this.root}/appraisal/categories/${id}`).pipe(this.unwrap(), map(r => r.data as AppraisalCategory));
  }
  createAppraisalCategory(body: AppraisalCategoryRequest): Observable<AppraisalCategory> {
    return this.http.post<ApiResponse<AppraisalCategory>>(`${this.root}/appraisal/categories`, body).pipe(this.unwrap(), map(r => r.data as AppraisalCategory));
  }
  updateAppraisalCategory(id: number, body: AppraisalCategoryRequest): Observable<AppraisalCategory> {
    return this.http.put<ApiResponse<AppraisalCategory>>(`${this.root}/appraisal/categories/${id}`, body).pipe(this.unwrap(), map(r => r.data as AppraisalCategory));
  }
  deleteAppraisalCategory(id: number): Observable<void> {
    return this.http.delete<ApiResponse>(`${this.root}/appraisal/categories/${id}`).pipe(this.unwrap(), map(() => undefined));
  }

  // ---- Leave: policies -----------------------------------------------------
  leavePolicies(query: PagedQuery = {}): Observable<PagedResult<LeavePolicy>> {
    return this.paged<LeavePolicy>(`${this.root}/leave/policies`, query);
  }
  getLeavePolicy(id: number): Observable<LeavePolicy> {
    return this.http.get<ApiResponse<LeavePolicy>>(`${this.root}/leave/policies/${id}`).pipe(this.unwrap(), map(r => r.data as LeavePolicy));
  }
  createLeavePolicy(body: LeavePolicyRequest): Observable<LeavePolicy> {
    return this.http.post<ApiResponse<LeavePolicy>>(`${this.root}/leave/policies`, body).pipe(this.unwrap(), map(r => r.data as LeavePolicy));
  }
  updateLeavePolicy(id: number, body: LeavePolicyRequest): Observable<LeavePolicy> {
    return this.http.put<ApiResponse<LeavePolicy>>(`${this.root}/leave/policies/${id}`, body).pipe(this.unwrap(), map(r => r.data as LeavePolicy));
  }
  deleteLeavePolicy(id: number): Observable<void> {
    return this.http.delete<ApiResponse>(`${this.root}/leave/policies/${id}`).pipe(this.unwrap(), map(() => undefined));
  }

  // ---- Leave: requests, calendar, balances -----------------------------------
  leaveRequests(query: LeaveRequestQuery = {}): Observable<PagedResult<LeaveRequest>> {
    return this.paged<LeaveRequest>(`${this.root}/leave/requests`, query);
  }
  getLeaveRequest(id: number): Observable<LeaveRequest> {
    return this.http.get<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests/${id}`).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  createLeaveRequest(body: LeaveRequestCreate): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests`, body).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  staffLeaveBalances(staffId: number, year: number): Observable<StaffLeaveBalance[]> {
    return this.http.get<ApiResponse<StaffLeaveBalance[]>>(`${this.staffs}/${staffId}/leave/balances`, { params: this.params({ year }) })
      .pipe(this.unwrap(), map(r => r.data ?? []));
  }
  relievingOfficers(staffId: number, search = ''): Observable<RelievingOfficerOption[]> {
    return this.http.get<ApiResponse<RelievingOfficerOption[]>>(`${this.staffs}/${staffId}/leave/relieving-officers`, { params: this.params({ search }) })
      .pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** Staff on a higher salary grade than this staff member (50 at most). Empty = no reviewer needed. Optional dropdown, so no error toast. */
  leaveReviewers(staffId: number, search = ''): Observable<RelievingOfficerOption[]> {
    return this.http.get<ApiResponse<RelievingOfficerOption[]>>(`${this.staffs}/${staffId}/leave/reviewers`, { params: this.params({ search }), headers: { 'suppress-error-toast': 'true' } })
      .pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** The apply dialog shows failures inline, so the global error toast is suppressed. */
  createStaffLeaveRequest(staffId: number, body: LeaveRequestCreate): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.staffs}/${staffId}/leave/requests`, body, { headers: { 'suppress-error-toast': 'true' } })
      .pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  decideLeaveRequest(id: number, decision: LeaveDecision, note?: string): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests/${id}/${decision}`, note ? { note } : {}).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  leaveCalendar(query: LeaveCalendarQuery): Observable<LeaveCalendarEntry[]> {
    return this.http.get<ApiResponse<LeaveCalendarEntry[]>>(`${this.root}/leave/calendar`, { params: this.params(query) }).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  leaveBalances(query: TeamBalancesQuery = {}): Observable<{ data: TeamBalances; pagination: ApiPagination }> {
    return this.http.get<ApiResponse<TeamBalances>>(`${this.root}/leave/balances`, { params: this.params(query) })
      .pipe(this.unwrap(), map(r => ({ data: r.data as TeamBalances, pagination: r.pagination as ApiPagination })));
  }

  // ---- Policy manager ------------------------------------------------------
  policyCategories(): Observable<PolicyCategory[]> {
    return this.http.get<ApiResponse<PolicyCategory[]>>(`${this.root}/policies/categories`).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  policies(query: StaffPolicyQuery = {}): Observable<PagedResult<StaffPolicy>> {
    return this.paged<StaffPolicy>(`${this.root}/policies`, query);
  }
  getPolicy(id: number): Observable<StaffPolicy> {
    return this.http.get<ApiResponse<StaffPolicy>>(`${this.root}/policies/${id}`).pipe(this.unwrap(), map(r => r.data as StaffPolicy));
  }
  createPolicy(body: StaffPolicyRequest): Observable<StaffPolicy> {
    return this.http.post<ApiResponse<StaffPolicy>>(`${this.root}/policies`, body).pipe(this.unwrap(), map(r => r.data as StaffPolicy));
  }
  updatePolicy(id: number, body: StaffPolicyRequest): Observable<StaffPolicy> {
    return this.http.put<ApiResponse<StaffPolicy>>(`${this.root}/policies/${id}`, body).pipe(this.unwrap(), map(r => r.data as StaffPolicy));
  }
  deletePolicy(id: number): Observable<void> {
    return this.http.delete<ApiResponse>(`${this.root}/policies/${id}`).pipe(this.unwrap(), map(() => undefined));
  }
  /** Signs the policy as the calling user. */
  acknowledgePolicy(id: number): Observable<PolicyAcknowledgement> {
    return this.http.post<ApiResponse<PolicyAcknowledgement>>(`${this.root}/policies/${id}/acknowledge`, {}).pipe(this.unwrap(), map(r => r.data as PolicyAcknowledgement));
  }
  complianceSummary(): Observable<ComplianceSummary> {
    return this.http.get<ApiResponse<ComplianceSummary>>(`${this.root}/policies/compliance/summary`).pipe(this.unwrap(), map(r => r.data as ComplianceSummary));
  }
  complianceRoster(query: ComplianceRosterQuery = {}): Observable<PagedResult<ComplianceRosterRow>> {
    return this.paged<ComplianceRosterRow>(`${this.root}/policies/compliance/roster`, query);
  }
  sendComplianceReminder(staffId: number): Observable<ComplianceReminderResult> {
    return this.http.post<ApiResponse<ComplianceReminderResult>>(`${this.root}/policies/compliance/staff/${staffId}/reminder`, {}).pipe(this.unwrap(), map(r => r.data as ComplianceReminderResult));
  }
  sendComplianceReminders(staffIds: number[]): Observable<ComplianceBulkReminderResult> {
    return this.http.post<ApiResponse<ComplianceBulkReminderResult>>(`${this.root}/policies/compliance/reminders`, { staffIds }).pipe(this.unwrap(), map(r => r.data as ComplianceBulkReminderResult));
  }
  /** CSV audit file (UTF-8 with BOM). */
  exportComplianceAudit(): Observable<{ blob: Blob; filename: string }> {
    return this.http.get(`${this.root}/policies/compliance/export`, { observe: 'response', responseType: 'blob' }).pipe(
      map(res => {
        const disposition = res.headers.get('Content-Disposition') || '';
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        return { blob: res.body as Blob, filename: match?.[1] || 'policy-compliance-audit.csv' };
      }),
      catchError(err => throwError(() => this.toError(err))),
    );
  }

  // ---- helpers -------------------------------------------------------------
  private paged<T>(url: string, query: object): Observable<PagedResult<T>> {
    return this.http.get<ApiResponse<T[]>>(url, { params: this.params(query) }).pipe(
      this.unwrap(),
      map(r => ({ data: r.data ?? [], pagination: r.pagination as ApiPagination })),
    );
  }

  private params(query: object): HttpParams {
    let params = new HttpParams();
    Object.entries(query || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return params;
  }

  /** Treats `successful: false` in a 2xx body as an error and normalises HTTP errors. */
  // ---- Appraisal rating scale ------------------------------------------------------------
  /** Wire shape of one rating band (`PUT /staff-module/appraisal/rating-scale`). */
  appraisalRatingScale(): Observable<AppraisalRatingScale> {
    return this.http.get<ApiResponse<any>>(`${this.root}/appraisal/rating-scale`).pipe(
      this.unwrap(),
      map(r => ({ minScore: Number(r.data?.minScore ?? 1), maxScore: Number(r.data?.maxScore ?? 5), bands: this.toBands(r.data), updatedBy: r.data?.updatedBy ?? null, lastUpdateTime: r.data?.lastUpdateTime ?? null })),
    );
  }
  saveAppraisalRatingScale(bands: AppraisalRatingBand[]): Observable<AppraisalRatingBand[]> {
    const ratings = bands.map(b => ({ from: b.minScore, to: b.maxScore, rating: b.label, remark: b.remark ?? '', color: b.color }));
    return this.http.put<ApiResponse<any>>(`${this.root}/appraisal/rating-scale`, { ratings }).pipe(this.unwrap(), map(r => this.toBands(r.data, bands)));
  }
  /** Accepts `{ ratings: [...] }` or a bare array; falls back to `fallback` when the response carries no bands. */
  private toBands(data: any, fallback: AppraisalRatingBand[] = []): AppraisalRatingBand[] {
    const rows: any[] = Array.isArray(data) ? data : data?.ratings;
    if (!Array.isArray(rows)) return fallback;
    return rows.map((r, i) => ({ id: r.id ?? i + 1, minScore: Number(r.from), maxScore: Number(r.to), label: r.rating, remark: r.remark ?? null, color: r.color ?? null }));
  }

  private unwrap<R extends ApiResponse<any>>() {
    return (source: Observable<R>): Observable<R> => source.pipe(
      map(res => {
        if (res && res.successful === false) throw this.toError({ status: res.code, error: res });
        return res;
      }),
      catchError(err => throwError(() => (err instanceof HttpErrorResponse ? this.toError(err) : err))),
    );
  }

  private friendlyMessage(status?: number): string {
    if (!status) return 'Unable to reach the server. Please check your connection and try again.';
    if (status === 404) return 'We could not find what you were looking for. Please try again later.';
    if (status === 401 || status === 403) return 'You do not have permission to do that.';
    if (status >= 500) return 'The server ran into a problem. Please try again in a moment.';
    return 'Something went wrong. Please try again.';
  }

  private toError(err: any): StaffModuleError {
    const body = err?.error;
    const fieldErrors = body && typeof body.data === 'object' && body.data !== null && !Array.isArray(body.data)
      ? body.data as Record<string, string>
      : undefined;
    // Never surface Angular's raw "Http failure response for <url>: 404 OK" text.
    const message = body?.message || this.friendlyMessage(err?.status);
    return { status: err?.status ?? 0, message, fieldErrors };
  }
}
